import type { FocusEvent, KeyboardEvent, RefCallback } from 'react';
import { useCallback, useReducer, useRef } from 'react';

import {
  INITIAL_NAV_MENU_DISCLOSURE_STATE,
  navMenuDisclosureReducer,
  resolveOpenNavMenuIndex,
} from './navMenuDisclosureReducer';

/**
 * ### NavMenuGroupHandlers
 *
 * Event handlers to spread onto the list item that wraps one primary link and
 * its submenu. Focus and blur bubble from the links inside, so the group is
 * the one element that sees every source of input.
 */
export interface NavMenuGroupHandlers {
  /**
   * ### onMouseOver
   *
   * Opens the group's submenu as the pointer enters it or any descendant.
   */
  onMouseOver: () => void;

  /**
   * ### onMouseLeave
   *
   * Releases the pointer's hold on the submenu.
   */
  onMouseLeave: () => void;

  /**
   * ### onFocus
   *
   * Opens the submenu when focus lands on the primary link or a submenu link.
   */
  onFocus: () => void;

  /**
   * ### onBlur
   *
   * Closes the submenu once focus moves to an element outside the group.
   * Focus moving between links inside the group is ignored.
   */
  onBlur: (event: FocusEvent<HTMLElement>) => void;

  /**
   * ### onKeyDown
   *
   * Escape closes the submenu and returns focus to the primary link.
   */
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * ### NavMenuDisclosure
 *
 * What {@link useNavMenuDisclosure} hands to the navigation for rendering.
 */
export interface NavMenuDisclosure {
  /**
   * ### openIndex
   *
   * Group whose submenu is shown, or `undefined` when all are closed.
   */
  openIndex: number | undefined;

  /**
   * ### closeAll
   *
   * Releases the pointer's hold on every submenu; attach to the `<nav>`
   * `onMouseLeave` so leaving the bar closes what hovering opened.
   */
  closeAll: () => void;

  /**
   * ### getGroupHandlers
   *
   * Handlers for the list item wrapping the group at `index`.
   */
  getGroupHandlers: (index: number) => NavMenuGroupHandlers;

  /**
   * ### getSubmenuMouseOver
   *
   * Handler for the submenu list itself, so moving the pointer from the
   * primary link down into the submenu keeps it open.
   */
  getSubmenuMouseOver: (index: number) => () => void;

  /**
   * ### getTriggerRef
   *
   * Ref for the primary link of the group at `index`; the element receives
   * focus again after Escape closes its submenu.
   */
  getTriggerRef: (index: number) => RefCallback<HTMLElement>;
}

/**
 * ### useNavMenuDisclosure
 *
 * Owns which primary navigation group shows its submenu, for pointer and
 * keyboard alike. Mouse behaviour is the classic hover menu; keyboard focus
 * inside a group opens it, focus leaving closes it, and Escape closes it while
 * restoring focus to the primary link.
 * The hook holds no route awareness: remount it (for example with a `key`) when
 * the page changes so a submenu opened by focus does not outlive the navigation.
 *
 * @returns Open state plus the handlers and refs the navigation spreads onto its elements
 * @see {@link navMenuDisclosureReducer} The state transitions behind the handlers
 */
export function useNavMenuDisclosure(): NavMenuDisclosure {
  const [state, dispatch] = useReducer(
    navMenuDisclosureReducer,
    INITIAL_NAV_MENU_DISCLOSURE_STATE,
  );
  const triggers = useRef(new Map<number, HTMLElement>());
  const triggerRefs = useRef(new Map<number, RefCallback<HTMLElement>>());

  const closeAll = useCallback(() => dispatch({ type: 'pointerLeave' }), []);

  const getGroupHandlers = useCallback(
    (index: number): NavMenuGroupHandlers => ({
      onMouseOver: () => dispatch({ type: 'pointerEnter', index }),
      onMouseLeave: () => dispatch({ type: 'pointerLeave' }),
      onFocus: () => dispatch({ type: 'focusEnter', index }),
      onBlur: (event) => {
        const next =
          event.relatedTarget instanceof Node ? event.relatedTarget : null;
        if (event.currentTarget.contains(next)) return;
        dispatch({ type: 'focusLeave', index });
      },
      onKeyDown: (event) => {
        if (event.key !== 'Escape') return;
        dispatch({ type: 'dismiss', index });
        triggers.current.get(index)?.focus();
      },
    }),
    [],
  );

  const getSubmenuMouseOver = useCallback(
    (index: number) => () => dispatch({ type: 'pointerEnter', index }),
    [],
  );

  const getTriggerRef = useCallback(
    (index: number): RefCallback<HTMLElement> => {
      const cached = triggerRefs.current.get(index);
      if (cached) return cached;

      // One stable callback per index so React does not detach and reattach it every render
      const ref: RefCallback<HTMLElement> = (element) => {
        if (element) triggers.current.set(index, element);
        else triggers.current.delete(index);
      };
      triggerRefs.current.set(index, ref);
      return ref;
    },
    [],
  );

  return {
    openIndex: resolveOpenNavMenuIndex(state),
    closeAll,
    getGroupHandlers,
    getSubmenuMouseOver,
    getTriggerRef,
  };
}
